export interface ReconnectTokenStore {
  read(): string | null;
  save(token: string): void;
  clear(): void;
}

const STORAGE_KEY_PREFIX = 'susdle.reconnectToken.';

/**
 * Resolves the Discord Activity instance id from the iframe URL. Discord
 * launches Activities with `?instance_id=...` in the iframe query string —
 * the same source `@discord/embedded-app-sdk` parses its synchronous
 * `instanceId` field from (see DiscordSdkPort.ts) — and dev-real uses the
 * same param. Resolved here so the zero-arg construction site in App.tsx
 * keeps working; callers that already hold a richer scope (devRealSession's
 * per-user dev tabs) pass it explicitly.
 */
function instanceIdFromUrl(): string {
  const fromUrl = new URL(window.location.href).searchParams.get('instance_id');
  return fromUrl !== null && fromUrl.length > 0 ? fromUrl : 'unknown-instance';
}

/**
 * localStorage-backed token store, keyed by Activity instance so concurrent
 * instances don't clobber each other's seats. localStorage (not
 * sessionStorage) because the token must outlive the iframe: the server holds
 * a dropped seat for 30 minutes (RECONNECTION_TTL_SECONDS), but sessionStorage
 * dies with the iframe — after a Discord crash/relaunch the player couldn't
 * reach their own reserved seat (joinOrCreate → GAME_IN_PROGRESS).
 *
 * Every storage access is guarded: in the cross-origin discordsays.com iframe
 * with third-party site data blocked (Chrome setting, Safari ITP), ANY
 * Storage touch throws SecurityError. The in-memory mirror keeps
 * within-session reconnects working when storage is blocked; only
 * iframe-teardown recovery is lost.
 */
export class LocalStorageReconnectStore implements ReconnectTokenStore {
  private readonly key: string;
  private fallback: string | null = null;

  constructor(scope: string = instanceIdFromUrl()) {
    this.key = STORAGE_KEY_PREFIX + scope;
  }

  read(): string | null {
    try {
      // `?? fallback` also covers the asymmetric-failure case where save()'s
      // setItem threw (quota) but getItem works — the mirror still has it.
      return window.localStorage.getItem(this.key) ?? this.fallback;
    } catch {
      return this.fallback;
    }
  }

  save(token: string): void {
    this.fallback = token;
    try {
      window.localStorage.setItem(this.key, token);
    } catch {
      /* blocked storage — token lives in memory for this page's lifetime */
    }
  }

  clear(): void {
    this.fallback = null;
    try {
      window.localStorage.removeItem(this.key);
    } catch {
      /* blocked storage — nothing persisted to remove */
    }
  }
}

/** App.tsx still constructs the store under its pre-localStorage name; the
 *  alias keeps that file untouched while a parallel change owns it.
 *  TODO(next App.tsx sweep): rename the call site and drop this alias. */
export { LocalStorageReconnectStore as SessionStorageReconnectStore };

export class InMemoryReconnectStore implements ReconnectTokenStore {
  private token: string | null = null;
  read(): string | null { return this.token; }
  save(token: string): void { this.token = token; }
  clear(): void { this.token = null; }
}
